import { useCallback, useReducer, useRef } from "react";

import useTooltipDismissFromTable, {
    type TooltipOpenChangeHandler,
} from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useTooltipDismissFromTable";

type OpenedFrom = "chip" | "badge";

type TooltipState = {
    isOpen: boolean;
    currentStep: number;
    openedFrom: OpenedFrom | null;
};

type TooltipAction =
    | { type: "openChip" }
    | { type: "openBadge" }
    | { type: "previous" }
    | { type: "next"; total: number }
    | { type: "close" };

const CLOSED_STATE: TooltipState = { isOpen: false, currentStep: 0, openedFrom: null };

const tooltipReducer = (state: TooltipState, action: TooltipAction): TooltipState => {
    switch (action.type) {
        case "openChip":
            return state.isOpen && state.openedFrom === "chip"
                ? CLOSED_STATE
                : { isOpen: true, currentStep: 0, openedFrom: "chip" };
        case "openBadge":
            return state.isOpen && state.openedFrom === "badge"
                ? CLOSED_STATE
                : { isOpen: true, currentStep: 1, openedFrom: "badge" };
        case "previous":
            return { ...state, currentStep: Math.max(0, state.currentStep - 1) };
        case "next":
            return { ...state, currentStep: Math.min(action.total - 1, state.currentStep + 1) };
        case "close":
            return CLOSED_STATE;
        default:
            return state;
    }
};

type UseLeadScoreTooltipParams = {
    total: number;
    onClick?: (isTooltipOpen: boolean) => void;
};

type LeadScoreTooltipController = TooltipState & {
    onChipClick: VoidFunction;
    onBadgeClick: VoidFunction;
    onPrevious: VoidFunction;
    onNext: VoidFunction;
    onClose: VoidFunction;
    onOpenChange: TooltipOpenChangeHandler;
};

const useLeadScoreTooltipController = ({ total, onClick }: UseLeadScoreTooltipParams): LeadScoreTooltipController => {
    const [state, dispatch] = useReducer(tooltipReducer, CLOSED_STATE);

    // Latest-refs so the open handlers stay referentially stable for the memoized chip/badge even
    // though the table cell passes a fresh `onClick` on every render. `stateRef` lets the handlers
    // compute the next open state (toggle semantics) without depending on `state`.
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    const stateRef = useRef(state);
    stateRef.current = state;

    const onChipClick = useCallback(() => {
        const willOpen = !(stateRef.current.isOpen && stateRef.current.openedFrom === "chip");
        onClickRef.current?.(willOpen);
        dispatch({ type: "openChip" });
    }, []);

    const onBadgeClick = useCallback(() => {
        const willOpen = !(stateRef.current.isOpen && stateRef.current.openedFrom === "badge");
        onClickRef.current?.(willOpen);
        dispatch({ type: "openBadge" });
    }, []);

    const onPrevious = useCallback(() => dispatch({ type: "previous" }), []);
    const onNext = useCallback(() => dispatch({ type: "next", total }), [total]);
    const onClose = useCallback(() => dispatch({ type: "close" }), []);

    const onOpenChange = useTooltipDismissFromTable(state.isOpen, onClose);

    return {
        ...state,
        onChipClick,
        onBadgeClick,
        onPrevious,
        onNext,
        onClose,
        onOpenChange,
    };
};

export default useLeadScoreTooltipController;
